import { AfterViewInit, Component, DestroyRef, Inject, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TypedRoute } from '@shared/utils';
import { ArivoPaymentMethod, BillingResolve, Money } from '@shared/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ArivoPaymentContainerComponent, ArivoPaymentIframeComponent, ArivoPaymentService } from '@shared/components';
import { AmplitudeEventService, CoreDataService, FeatureFlagService } from '@core/services';
import { PaymentMethodAddClicked, PaymentMethodAddFailed, PaymentMethodAddSucceeded } from '@integrations/amplitude';
import { ArivoBottomSheetDialogComponent } from '@shared/components/ui-elements';

@Component({
  selector: 'app-billing-account-balance',
  templateUrl: './billing-account-balance.component.html',
  styleUrl: './billing-account-balance.component.scss',
})
export class BillingAccountBalanceComponent implements OnInit, AfterViewInit {
  @ViewChild('paymentContainer') paymentContainer!: ArivoPaymentContainerComponent | ArivoPaymentIframeComponent;
  @ViewChild('billingPaymentMethodDialog') billingPaymentMethodDialog!: ArivoBottomSheetDialogComponent;
  private destroyRef = inject(DestroyRef);
  paymentMethod: ArivoPaymentMethod | null = null;
  openCosts: Money[] = [];
  creditCardIconClass: string | null = null;
  adyenIframeEnabled: boolean = false;
  autoInitPaymentForm: boolean = false;
  initialized: boolean = false;
  showPayPerUse: boolean = false;

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<BillingResolve>,
    private router: Router,
    private paymentService: ArivoPaymentService,
    private coreDataService: CoreDataService,
    private featureFlagService: FeatureFlagService,
    private amplitudeEventService: AmplitudeEventService,
  ) {}

  get hasExternalPaymentMethod(): boolean {
    return this.coreDataService.coreData.value.customer_settings?.has_external_payment_method;
  }

  ngOnInit(): void {
    this.adyenIframeEnabled = this.featureFlagService.getFeatureFlag('enable_iframe_adyen');
    this.showPayPerUse = this.featureFlagService.getFeatureFlag('enable_ppu') === true;

    if (this.adyenIframeEnabled) {
      this.paymentContainer = this.paymentContainer as ArivoPaymentIframeComponent;
    } else {
      this.paymentContainer = this.paymentContainer as ArivoPaymentContainerComponent;
    }

    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.openCosts = data.billingApiResponse.open_costs;
      this.paymentMethod = data.billingApiResponse.payment_method;
      this.coreDataService.setPaymentMethodAvailability(!!this.paymentMethod);
      if (this.paymentMethod?.brand) {
        this.creditCardIconClass = this.paymentService.getCreditCardIcon(this.paymentMethod.brand);
      }
    });
    this.paymentService.activePaymentMethod.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((paymentMethod) => {
      if (paymentMethod) {
        this.paymentMethod = paymentMethod;
        if (this.paymentMethod?.brand) {
          this.creditCardIconClass = this.paymentService.getCreditCardIcon(this.paymentMethod.brand);
        }
      }
    });
  }

  ngAfterViewInit() {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const showPaymentFormStatus = params.get('showPaymentForm');
      if (showPaymentFormStatus === 'true' && !this.hasExternalPaymentMethod) {
        if (this.adyenIframeEnabled && !this.initialized) {
          this.autoInitPaymentForm = true;
        } else {
          this.paymentContainer.setUpComponents();
        }
        this.removeShowPaymentFormParam();
      }
      this.initialized = true;
    });
  }

  removeShowPaymentFormParam(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { showPaymentForm: null },
      queryParamsHandling: 'merge',
    });
  }

  setupPaymentContainer() {
    this.paymentContainer.setUpComponents();
    this.sendAddPaymentMethodClickedToAmplitude();
  }

  sendAddPaymentMethodClickedToAmplitude() {
    this.amplitudeEventService.trackEvent(
      new PaymentMethodAddClicked({
        paymentMethodChangeType: this.paymentMethod ? 'update' : 'add',
      }),
    );
  }
  handlePaymentMethodUpdateFailed() {
    this.billingPaymentMethodDialog.close();
    this.amplitudeEventService.trackEvent(
      new PaymentMethodAddFailed({
        paymentMethodChangeType: this.paymentMethod ? 'update' : 'add',
        adyenIntegrationType: this.adyenIframeEnabled ? 'iframe' : 'direct',
      }),
    );
  }
  handlePaymentMethodUpdateSucceeded() {
    this.billingPaymentMethodDialog.close();
    if (!this.paymentMethod) {
      return;
    }
    this.amplitudeEventService.trackEvent(
      new PaymentMethodAddSucceeded({
        paymentMethodChangeType: this.paymentMethod ? 'update' : 'add',
        paymentMethod: this.paymentMethod.type,
        adyenIntegrationType: this.adyenIframeEnabled ? 'iframe' : 'direct',
      }),
    );
  }
}
